import { useCallback, useEffect, useRef, useState } from 'react';

/** Devuelve Date.now() y lo actualiza cada `ms` (para "hace X min" y relojes). */
export function useAhora(ms = 30_000): number {
  const [ahora, setAhora] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setAhora(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return ahora;
}

export interface Carga<T> {
  datos: T | null;
  error: string | null;
  cargando: boolean;
  recargar: () => void;
}

/**
 * Carga asíncrona con manejo de carrera: si cambian las dependencias antes de que
 * vuelva la respuesta, se descarta la vieja. `null` en `fn` = no cargar todavía.
 */
export function useCarga<T>(fn: (() => Promise<T>) | null, deps: readonly unknown[]): Carga<T> {
  const [datos, setDatos] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(fn !== null);
  const [n, setN] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    const f = fnRef.current;
    if (!f) {
      setCargando(false);
      return;
    }
    let vigente = true;
    setCargando(true);
    setError(null);
    f().then(
      (d) => {
        if (!vigente) return;
        setDatos(d);
        setCargando(false);
      },
      (e: unknown) => {
        if (!vigente) return;
        setError(e instanceof Error ? e.message : 'Ocurrió un error inesperado.');
        setCargando(false);
      },
    );
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, n]);

  const recargar = useCallback(() => setN((x) => x + 1), []);
  return { datos, error, cargando, recargar };
}

/** Valor con retraso, para no pedir a la API en cada tecla. */
export function useDebounce<T>(valor: T, ms = 300): T {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}
